<script setup lang="ts">
import Typography from '@components/Typography.vue';
import { computed } from 'vue';
import type { Url } from '@global-types/url.ts';
import type { PipedriveUser } from '@models/groups/app';
import ProfilePicture from '@components/ProfilePicture.vue';
import PipedriveUserPicture from '@components/PipedriveUserPicture.vue';
import { trans, transchoice } from '@tools/translation.ts';
import { storeToRefs } from 'pinia';
import { useScreensizeStore } from '@stores/useScreensizeStore';

const props = defineProps<{
  accountManager: PipedriveUser | null;
}>();

const { isMobileOrTablet } = storeToRefs(useScreensizeStore());

const cdnUrl = computed<Url>(() => window.CDN_URL);
const companyPhone = computed<string>(() => window.COMPANY_PHONE);
const contactMail = computed<string>(() => window.CONTACT_MAIL);

const title = computed<string>(() => (props.accountManager
  ? transchoice('user_menu.account_manager.contact', { account_manager: props.accountManager.firstname })
  : trans('user_menu.account_manager.unqualified')));

const phoneNumber = computed<string|null>(() => (props.accountManager
  ? props.accountManager.phone_number
  : companyPhone.value));

const email = computed<string>(() => props.accountManager?.email ?? contactMail.value);
</script>

<template>
  <div class="flex items-center space-x-2">
    <PipedriveUserPicture
      v-if="accountManager"
      class="shrink-0"
      :pipedrive-user="accountManager"
      :size="48"
    />

    <ProfilePicture
      v-else
      class="menu-account-manager-contact__photo"
      :class="[
        'shrink-0',
        'rounded-full',
        'size-12',
        'overflow-hidden',
      ]"
      :url="`${cdnUrl}/images/company-avatar.svg`"
    />

    <div class="flex flex-col">
      <Typography
        :variant="isMobileOrTablet ? 'content-heading-2' : 'body-small-bold'"
        class="whitespace-nowrap"
      >
        {{ title }}
      </Typography>

      <div class="flex space-x-4 lg:flex-col lg:space-x-0">
        <a
          v-if="phoneNumber"
          :href="`tel:${phoneNumber?.replace(/ /g, '')}`"
        >
          <Typography
            :variant="isMobileOrTablet ? 'body' : 'body-small'"
            class="underline"
          >
            {{ phoneNumber }}
          </Typography>
        </a>

        <a :href="`mailto:${email}`">
          <Typography
            :variant="isMobileOrTablet ? 'body' : 'body-small'"
            class="underline"
          >
            {{ email }}
          </Typography>
        </a>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@styles/mixins/images' as *;
@use '@styles/mixins/rem' as *;

.menu-account-manager-contact {
  // @todo : adapt to new component
  &__photo {
    @include profilePicture(48);
  }
}
</style>
